import { type AxialCoord, hexDistance, hexEquals } from "../hex";
import { structureComplete, workingCities } from "../land/cities";
import { canTake } from "../land/territory";
import { applyFirst, nearestTo, rankBy } from "../pick";
import { findHex, unitAt, unitsOf } from "../run/board";
import { kingOf } from "../run/pools";
import { scriptUnitsOf } from "../run/seats";
import { allied, hostile, sameOwner } from "../run/sides";
import type { CourtRules } from "../types/rules";
import type { FactionId, RunState, Unit } from "../types/state";
import { aimCommander, commandersOf, promoteCommander } from "./commanders";
import { canMark, marksOf, raiseTax, setCall, setMark } from "./general";
import { commandRefusal, inKingReach, onThrone, trust } from "./king";

/**
 * The general's script (A33-A42, C2.1): the scripted calls and banners, and the promotions and aims of
 * a scripted King's commanders.
 */

/** The own working city nearest the capital with a rival unit within `sight`, no banner on it yet, in the banners' range. */
function threatenedCity(
  state: RunState,
  faction: FactionId,
  sight: number,
): AxialCoord | undefined {
  const king = kingOf(state, faction);
  if (!king || sight <= 0) {
    return undefined;
  }
  const rivals = state.units.filter((unit) => hostile(state.rules, unit.owner, faction));
  const cities = workingCities(state, faction).filter(
    (city) =>
      canMark(state, faction, city.coord) &&
      rivals.some((unit) => hexDistance(unit.coord, city.coord) <= sight),
  );
  return nearestTo(cities, king.capital)?.coord;
}

/**
 * The script's general (A33-A34, A39), with its Will pool full. From its throne it raises the
 * tax when it is ready and calls its kingdom to capture while open neutral land is left. After
 * that, from anywhere, it plants a banner on the rivals' working city nearest its capital that
 * its banners reach (their nearest hex when none does).
 */
export function scriptedCall(state: RunState, faction: FactionId): RunState {
  const general = state.rules.court?.general;
  const king = kingOf(state, faction);
  if (!general?.scriptCalls || !king) {
    return state;
  }
  // Only from a full pool, so the general never starves the captain's orders: measured on the
  // pace seeds, mirror decisive 35/40 against 32 without this rule (docs/INCREMENT-5.7-AMENDMENTS.md).
  if (king.will < trust(state, faction)) {
    return state;
  }
  const besieged = threatenedCity(state, faction, general.scriptDefendSight);
  if (besieged) {
    return setMark(state, faction, besieged);
  }
  const throned = onThrone(state, faction);
  const taxed = throned ? raiseTax(state, faction) : state;
  const openLand = taxed.hexes.some((hex) => hex.owner === "none" && !hex.razed && hex.walls === 0);
  if (openLand) {
    return throned ? setCall(taxed, faction) : taxed;
  }
  const foreign = taxed.hexes.filter(
    (hex) =>
      hostile(taxed.rules, hex.owner, faction) &&
      !hex.razed &&
      hexDistance(hex.coord, king.coord) <= general.markRange,
  );
  const mark =
    nearestTo(
      foreign.filter((hex) => hex.structure === "city" && structureComplete(hex, taxed.tick)),
      king.capital,
    ) ?? nearestTo(foreign, king.capital);
  return mark ? setMark(taxed, faction, mark.coord) : taxed;
}

/**
 * The hex the script's King wants his squads' banner on now (A66); undefined while an attack
 * banner of his flies within `commanderSight` of a commander of his, without a commander,
 * and when no land fits. The land: not his kingdom's, to be taken by standing, in the banners'
 * range, without a banner and without a unit of his on it (that hex is being taken already:
 * a banner there would be met before anybody could answer it); the nearest to his first
 * commander that has any, then map order.
 */
function bannerWanted(state: RunState, faction: FactionId): AxialCoord | undefined {
  const court = state.rules.court;
  if (!court || !state.rules.company?.scriptBanners || !kingOf(state, faction)) {
    return undefined;
  }
  const leaders = commandersOf(state, faction);
  const marks = marksOf(state, faction);
  const flying = marks.some(
    (mark) =>
      mark.kind === "attack" &&
      leaders.some((leader) => hexDistance(leader.coord, mark.target) <= court.commanderSight),
  );
  if (flying) {
    return undefined;
  }
  for (const leader of leaders) {
    const land = nearestTo(
      state.hexes.filter(
        (hex) =>
          canTake(state, faction, hex.coord) &&
          !sameOwner(unitAt(state, hex.coord)?.owner, faction) &&
          !marks.some((mark) => hexEquals(mark.target, hex.coord)) &&
          commandRefusal(state, faction, hex.coord, "mark") !== "reach",
      ),
      leader.coord,
    );
    if (land) {
      return land.coord;
    }
  }
  return undefined;
}

/**
 * A66: the script's King plants the banner his squads march under, for the banner's Will. Same
 * object back when he wants none or is short of its price.
 */
export function scriptedBanner(state: RunState, faction: FactionId): RunState {
  const land = bannerWanted(state, faction);
  return land ? setMark(state, faction, land) : state;
}

/** Whether the script's King holds every other command back for his banner: he wants one, and his Trust can hold its price. */
export function waitsForBanner(state: RunState, faction: FactionId): boolean {
  const court = state.rules.court;
  const king = kingOf(state, faction);
  return (
    court !== null &&
    king !== undefined &&
    king.will < court.costs.mark &&
    trust(state, faction) >= court.costs.mark &&
    bannerWanted(state, faction) !== undefined
  );
}

/**
 * Commanders the script wants: one for every `scriptCitiesPerCommander` working cities,
 * rounded down, so a one-city King settles his land by hand before anybody goes to war.
 */
function commandersWanted(state: RunState, faction: FactionId, court: CourtRules): number {
  return Math.floor(workingCities(state, faction).length / court.scriptCitiesPerCommander);
}

/** The nearest commander of the faction that still has room, counting the soldiers already sent to it. */
export function musteringCommander(
  state: RunState,
  faction: FactionId,
  from: AxialCoord,
): Unit | undefined {
  const court = state.rules.court;
  if (!court) {
    return undefined;
  }
  const coming = (commander: Unit): number =>
    unitsOf(state, faction).filter(
      (unit) =>
        unit.squadOf === commander.id ||
        (unit.squadOf === undefined &&
          unit.destination !== undefined &&
          hexEquals(unit.destination, commander.coord)),
    ).length;
  return nearestTo(
    commandersOf(state, faction),
    from,
    (commander) => coming(commander) < court.squadSize,
  );
}

/** The unit the script would promote now: the healthiest the court lets it, then the lower id. */
export function promotionCandidate(state: RunState, faction: FactionId): Unit | undefined {
  const court = state.rules.court;
  if (
    !court ||
    commandersOf(state, faction).length >= commandersWanted(state, faction, court) ||
    unitsOf(state, faction).length < 2
  ) {
    return undefined;
  }
  const [healthiest] = rankBy(
    scriptUnitsOf(state, faction).filter(
      (unit) =>
        !unit.commander && unit.squadOf === undefined && inKingReach(state, faction, unit.coord),
    ),
    (unit) => -unit.hp,
    (unit) => unit.id,
  );
  return healthiest;
}

/** The script's King promotes the healthiest soldier in his reach while he has fewer commanders than he wants. */
export function scriptedPromotions(state: RunState, faction: FactionId): RunState {
  const candidate = promotionCandidate(state, faction);
  return candidate ? promoteCommander(state, faction, candidate.id) : state;
}

/** A commander with nothing left to march on: never aimed, or its target is its faction's or burned. */
export function awaitsAim(state: RunState, commander: Unit): boolean {
  const aimed = commander.target && findHex(state, commander.target);
  return !aimed || aimed.razed || aimed.owner === commander.owner;
}

/**
 * The script's King aims every commander that has no target, or whose target is taken, at the
 * nearest city of the other faction, else its nearest hex, else the nearest neutral hex; hex
 * distance from the commander, then insertion order, and only where a road exists.
 */
export function scriptedAims(state: RunState, faction: FactionId): RunState {
  let next = state;
  for (const commander of commandersOf(state, faction)) {
    if (
      !awaitsAim(next, commander) ||
      commandRefusal(next, faction, commander.coord, "aim") !== undefined
    ) {
      continue;
    }
    const ranked = rankBy(
      next.hexes.filter((hex) => !hex.razed && !allied(next.rules, hex.owner, faction)),
      (hex) => (hex.owner === "none" ? 2 : hex.structure === "city" ? 0 : 1),
      (hex) => hexDistance(hex.coord, commander.coord),
    );
    next = applyFirst(next, ranked, (from, hex) =>
      aimCommander(from, faction, commander.id, hex.coord),
    );
  }
  return next;
}
